import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Editor } from "@tiptap/react";

import {
  fetchTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  createTemplateVersion,
  updateTemplateVersion,
  deleteTemplateVersion,
  cloneTemplateVersion,
  fetchTemplateVariables,
  fetchSystemTemplates,
  fetchSystemTemplateBody,
  updateSystemTemplateBody,
  type SystemTemplateInfo,
} from "@/api/crm";
import type { EmailTemplate, EmailTemplateVersion } from "@/types/crm";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { Button } from "@/components/ui/button";
import { useUnifiedErrorSurface } from "@/hooks/useUnifiedErrorSurface";
import { cn } from "@/lib/utils";
import { useCapabilities } from "@/hooks/useCapabilities";
import LockedFeatureCard from "@/components/pricing/LockedFeatureCard";
import { DetectedVariableBadges } from "./DetectedVariableBadges";

const initialTemplatesState = { data: [] as EmailTemplate[], loading: false, error: null as string | null };
type TemplateVariableType = "invoice" | "press" | "settlement" | "shopify" | "crm";
const DEFAULT_CHANNELS = ["email"];
const VARIABLE_TYPES: TemplateVariableType[] = ["invoice", "press", "settlement", "shopify", "crm"];
const TOKEN_REGEX = /\{\{\s*([a-zA-Z0-9._-]+)\s*\}\}/g;
const BRACKET_TOKEN_REGEX = /\{\{\s*([a-zA-Z0-9._-]+)\s*\}\}/g;

function extractTemplateTokens(...values: (string | null)[]): string[] {
  const tokens = new Set<string>();
  values.forEach((value) => {
    if (!value) return;
    const regex = new RegExp(TOKEN_REGEX);
    let match: RegExpExecArray | null;
    while ((match = regex.exec(value)) !== null) {
      tokens.add(match[1]);
    }
  });
  return Array.from(tokens);
}

function lintTemplateTokens(subject: string, body: string, registry: Record<string, string>): string[] {
  if (!Object.keys(registry).length) return [];
  const tokens = extractTemplateTokens(subject, body);
  return tokens.filter((token) => !registry[token]);
}

function validateTemplateSyntax(subject: string, body: string): string[] {
  const errors: string[] = [];
  const check = (value: string, label: string) => {
    const openMustache = (value.match(/{{/g) ?? []).length;
    const closeMustache = (value.match(/}}/g) ?? []).length;
    if (openMustache !== closeMustache) {
      errors.push(`${label} has unmatched {{ }} tokens.`);
    }
  };
  check(subject, "Subject");
  check(body, "Body");
  return errors;
}

function buildPreview(content: string, registry: Record<string, string>) {
  return content.replace(BRACKET_TOKEN_REGEX, (_, token) => registry[token] ?? `{{ ${token} }}`);
}

type TemplateManagerProps = {
  onTemplatesChanged?: () => void;
};

export function TemplateManager({ onTemplatesChanged }: TemplateManagerProps = {}) {
  const templateBodyEditorRef = useRef<Editor | null>(null);
  const versionBodyEditorRef = useRef<Editor | null>(null);
  const [templatesState, setTemplatesState] = useState(initialTemplatesState);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [templateForm, setTemplateForm] = useState({ name: "", persona: "", channel: "email", subject: "", body: "" });
  const [templateEditValues, setTemplateEditValues] = useState({ name: "", persona: "", channel: "email" });
  const [isEditingTemplate, setIsEditingTemplate] = useState(false);
  const [versionEditingId, setVersionEditingId] = useState<number | null>(null);
  const [versionForm, setVersionForm] = useState({ subject: "", body: "", active: true });
  const [templateActionMessage, setTemplateActionMessage] = useState<string | null>(null);
  const [creatorChannel, setCreatorChannel] = useState<string | null>(null);
  const [customTypes, setCustomTypes] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem("crmTemplateTypes");
      if (stored) return JSON.parse(stored) as string[];
    } catch {
      return [];
    }
    return [];
  });
  const [newTypeInput, setNewTypeInput] = useState("");
  const [expandedChannels, setExpandedChannels] = useState<Set<string>>(new Set(DEFAULT_CHANNELS));

  const [systemTemplates, setSystemTemplates] = useState<SystemTemplateInfo[]>([]);
  const [systemListLoading, setSystemListLoading] = useState(false);
  const [activeSystemId, setActiveSystemId] = useState<string | null>(null);
  const [systemContentMap, setSystemContentMap] = useState<Record<string, string>>({});
  const [systemLoadingId, setSystemLoadingId] = useState<string | null>(null);
  const [systemSavingId, setSystemSavingId] = useState<string | null>(null);
  const [systemMessage, setSystemMessage] = useState<string | null>(null);
  const { notifyError } = useUnifiedErrorSurface();
  const { capabilities, openUpgradeModal, hasFullAccess } = useCapabilities();
  const allowTemplateAutomation = capabilities.canUseTemplateAutomation;
  const canManageSystemTemplates =
    hasFullAccess || Boolean(capabilities.canEditWorkflows || capabilities.canCreateWorkflows);

  const [variables, setVariables] = useState<Record<string, string>>({});
  const [variablesLoading, setVariablesLoading] = useState(false);
  const [variablesError, setVariablesError] = useState<string | null>(null);
  const registryReady = Object.keys(variables).length > 0;

  useEffect(() => {
    localStorage.setItem("crmTemplateTypes", JSON.stringify(customTypes));
  }, [customTypes]);

  const loadTemplates = useCallback(async () => {
    setTemplatesState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await fetchTemplates(true);
      setTemplatesState({ data, loading: false, error: null });
      if (
        data.length &&
        selectedTemplateId !== null &&
        !data.some((template) => template.id === selectedTemplateId)
      ) {
        setSelectedTemplateId(data[0].id);
      }
    } catch (error) {
      const message = (error as Error)?.message ?? "Unable to load templates.";
      setTemplatesState((prev) => ({
        ...prev,
        loading: false,
        error: message,
      }));
      notifyError(error, "Template load failed");
    }
  }, [selectedTemplateId, notifyError]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const loadSystemTemplates = useCallback(async () => {
    if (!allowTemplateAutomation || !canManageSystemTemplates) {
      setSystemTemplates([]);
      setSystemMessage(null);
      setSystemListLoading(false);
      return;
    }
    setSystemListLoading(true);
    try {
      const entries = await fetchSystemTemplates();
      setSystemTemplates(entries);
    } catch (error) {
      const message = (error as Error)?.message ?? "Unable to load system templates.";
      setSystemMessage(message);
      notifyError(error, "System template load failed");
    } finally {
      setSystemListLoading(false);
    }
  }, [allowTemplateAutomation, canManageSystemTemplates, notifyError]);

  useEffect(() => {
    if (!allowTemplateAutomation || !canManageSystemTemplates) return;
    void loadSystemTemplates();
  }, [allowTemplateAutomation, canManageSystemTemplates, loadSystemTemplates]);

  const loadSystemBody = useCallback(
    async (templateId: string, force = false) => {
      if (!canManageSystemTemplates) return;
      if (!force && systemContentMap[templateId]) return;
      setSystemLoadingId(templateId);
      setSystemMessage(null);
      try {
        const payload = await fetchSystemTemplateBody(templateId);
        setSystemContentMap((prev) => ({ ...prev, [templateId]: payload.body ?? "" }));
      } catch (error) {
        const message = (error as Error)?.message ?? "Unable to load system template.";
        setSystemMessage(message);
        notifyError(error, "System template error");
      } finally {
        setSystemLoadingId(null);
      }
    },
    [systemContentMap, canManageSystemTemplates, notifyError],
  );

  useEffect(() => {
    if (activeSystemId && canManageSystemTemplates) {
      void loadSystemBody(activeSystemId);
    } else if (!canManageSystemTemplates && activeSystemId) {
      setActiveSystemId(null);
    }
  }, [activeSystemId, canManageSystemTemplates, loadSystemBody]);

  const loadVariables = useCallback(
    async () => {
      setVariablesLoading(true);
      setVariablesError(null);
      try {
        const results = await Promise.allSettled(
          VARIABLE_TYPES.map(async (type) => {
            const payload = await fetchTemplateVariables(type);
            return payload?.variables ?? {};
          }),
        );
        const merged: Record<string, string> = {};
        const failures: string[] = [];
        results.forEach((result, index) => {
          if (result.status === "fulfilled") {
            Object.entries(result.value).forEach(([key, value]) => {
              if (!(key in merged)) {
                merged[key] = value;
              }
            });
          } else {
            failures.push(VARIABLE_TYPES[index]);
          }
        });
        setVariables(merged);
        if (failures.length) {
          setVariablesError(`Some variable groups failed to load: ${failures.join(", ")}.`);
        }
      } catch (error) {
        const message = (error as Error)?.message ?? "Unable to load variable registry.";
        setVariablesError(message);
        notifyError(error, "Variable registry error");
      } finally {
        setVariablesLoading(false);
      }
    },
    [notifyError],
  );

  useEffect(() => {
    if (!allowTemplateAutomation) return;
    void loadVariables();
  }, [allowTemplateAutomation, loadVariables]);

  const selectedTemplate = useMemo(
    () => templatesState.data.find((template) => template.id === selectedTemplateId) ?? null,
    [templatesState.data, selectedTemplateId],
  );

  const versionsForTemplate = selectedTemplate?.versions ?? [];
  const creationLint = useMemo(() => {
    if (!allowTemplateAutomation || !creatorChannel || !registryReady) return [];
    return lintTemplateTokens(templateForm.subject, templateForm.body, variables);
  }, [allowTemplateAutomation, creatorChannel, registryReady, templateForm, variables]);

  const creationSyntaxErrors = useMemo(
    () => (allowTemplateAutomation ? validateTemplateSyntax(templateForm.subject, templateForm.body) : []),
    [allowTemplateAutomation, templateForm.body, templateForm.subject],
  );
  const creationTokens = useMemo(
    () => (allowTemplateAutomation ? extractTemplateTokens(templateForm.subject, templateForm.body) : []),
    [allowTemplateAutomation, templateForm.subject, templateForm.body],
  );

  const versionLint = useMemo(() => {
    if (!allowTemplateAutomation || !versionEditingId || !registryReady) return [];
    return lintTemplateTokens(versionForm.subject, versionForm.body, variables);
  }, [allowTemplateAutomation, versionEditingId, registryReady, versionForm, variables]);
  const versionTokens = useMemo(
    () => (allowTemplateAutomation ? extractTemplateTokens(versionForm.subject, versionForm.body) : []),
    [allowTemplateAutomation, versionForm.subject, versionForm.body],
  );
  const versionSyntaxErrors = useMemo(
    () => (allowTemplateAutomation ? validateTemplateSyntax(versionForm.subject, versionForm.body) : []),
    [allowTemplateAutomation, versionForm.body, versionForm.subject],
  );

  useEffect(() => {
    if (selectedTemplate) {
      setTemplateEditValues({
        name: selectedTemplate.name,
        persona: selectedTemplate.persona ?? "",
        channel: selectedTemplate.channel,
      });
      setVersionEditingId(null);
      setTemplateActionMessage(null);
    }
  }, [selectedTemplate?.id]);

  useEffect(() => {
    if (creatorChannel) {
      setTemplateForm((prev) => ({ ...prev, channel: creatorChannel }));
    }
  }, [creatorChannel]);

  const handleCreateTemplate = useCallback(async () => {
    if (!templateForm.name.trim() || !templateForm.subject.trim()) return;
    const payload = {
      ...templateForm,
      channel: creatorChannel ?? templateForm.channel ?? "email",
    };
    try {
      const created = await createTemplate(payload);
      setTemplateForm({ name: "", persona: "", channel: payload.channel, subject: "", body: "" });
      setCreatorChannel(null);
      setTemplateActionMessage("Template created");
      setSelectedTemplateId(created.id);
      onTemplatesChanged?.();
      loadTemplates();
    } catch (error) {
      const message = (error as Error)?.message ?? "Unable to create template";
      setTemplateActionMessage(message);
      notifyError(error, "Template creation failed");
    }
  }, [templateForm, creatorChannel, loadTemplates, notifyError, onTemplatesChanged]);

  const handleUpdateTemplate = useCallback(async () => {
    if (!selectedTemplate) return;
    try {
      await updateTemplate(selectedTemplate.id, templateEditValues);
      setIsEditingTemplate(false);
      setTemplateActionMessage("Template updated");
      onTemplatesChanged?.();
      loadTemplates();
    } catch (error) {
      const message = (error as Error)?.message ?? "Unable to update template";
      setTemplateActionMessage(message);
      notifyError(error, "Template update failed");
    }
  }, [selectedTemplate, templateEditValues, loadTemplates, notifyError, onTemplatesChanged]);

  const handleDeleteTemplate = useCallback(async () => {
    if (!selectedTemplate) return;
    if (!window.confirm(`Delete template “${selectedTemplate.name}”?`)) return;
    try {
      await deleteTemplate(selectedTemplate.id);
      setTemplateActionMessage("Template deleted");
      setSelectedTemplateId(null);
      onTemplatesChanged?.();
      loadTemplates();
    } catch (error) {
      const message = (error as Error)?.message ?? "Unable to delete template";
      setTemplateActionMessage(message);
      notifyError(error, "Template delete failed");
    }
  }, [selectedTemplate, loadTemplates, notifyError, onTemplatesChanged]);

  const handleCloneVariant = useCallback(async () => {
    if (!selectedTemplate) return;
    try {
      await cloneTemplateVersion(selectedTemplate.id);
      setTemplateActionMessage("Variant generated");
      onTemplatesChanged?.();
      loadTemplates();
    } catch (error) {
      const message = (error as Error)?.message ?? "Unable to generate variant";
      setTemplateActionMessage(message);
      notifyError(error, "Variant generation failed");
    }
  }, [selectedTemplate, loadTemplates, notifyError, onTemplatesChanged]);

  const startVersionEdit = (version: EmailTemplateVersion) => {
    setVersionEditingId(version.id);
    setVersionForm({
      subject: version.subject_template ?? "",
      body: version.body_template ?? "",
      active: version.active,
    });
  };

  const handleVersionUpdate = useCallback(async () => {
    if (!selectedTemplate || !versionEditingId) return;
    try {
      await updateTemplateVersion(selectedTemplate.id, versionEditingId, {
        subject: versionForm.subject,
        body: versionForm.body,
        active: versionForm.active,
      });
      setVersionEditingId(null);
      setTemplateActionMessage("Version updated");
      onTemplatesChanged?.();
      loadTemplates();
    } catch (error) {
      const message = (error as Error)?.message ?? "Unable to update version";
      setTemplateActionMessage(message);
      notifyError(error, "Version update failed");
    }
  }, [selectedTemplate, versionEditingId, versionForm, loadTemplates, notifyError, onTemplatesChanged]);

  const handleDeleteVersion = useCallback(
    async (versionId: number) => {
      if (!selectedTemplate) return;
      if (!window.confirm("Delete this version?")) return;
      try {
        await deleteTemplateVersion(selectedTemplate.id, versionId);
        setTemplateActionMessage("Version deleted");
        onTemplatesChanged?.();
        loadTemplates();
      } catch (error) {
        const message = (error as Error)?.message ?? "Unable to delete version";
        setTemplateActionMessage(message);
        notifyError(error, "Version delete failed");
      }
    },
    [selectedTemplate, loadTemplates, notifyError, onTemplatesChanged],
  );

  const handleSystemSave = useCallback(async () => {
    if (!activeSystemId || !canManageSystemTemplates) return;
    setSystemSavingId(activeSystemId);
    setSystemMessage(null);
    try {
      await updateSystemTemplateBody(activeSystemId, systemContentMap[activeSystemId] ?? "");
      setSystemMessage("Template saved");
      onTemplatesChanged?.();
    } catch (error) {
      const message = (error as Error)?.message ?? "Unable to save system template.";
      setSystemMessage(message);
      notifyError(error, "System save failed");
    } finally {
      setSystemSavingId(null);
    }
  }, [activeSystemId, canManageSystemTemplates, systemContentMap, notifyError, onTemplatesChanged]);

  const templatesByChannel = useMemo(() => {
    const groups: Record<string, EmailTemplate[]> = {};
    templatesState.data.forEach((template) => {
      const channel = template.channel || "email";
      if (channel.toLowerCase() === "sms") return;
      if (!groups[channel]) groups[channel] = [];
      groups[channel].push(template);
    });
    Object.values(groups).forEach((items) => {
      items.sort((a, b) => a.name.localeCompare(b.name));
    });
    return groups;
  }, [templatesState.data]);

  const channelTypes = useMemo(() => {
    const set = new Set<string>(DEFAULT_CHANNELS);
    Object.keys(templatesByChannel).forEach((channel) => set.add(channel));
    customTypes.forEach((type) => set.add(type));
    set.delete("sms");
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [templatesByChannel, customTypes]);

  const expandChannel = (channel: string) => {
    setExpandedChannels((prev) => {
      const next = new Set(prev);
      if (next.has(channel)) {
        next.delete(channel);
      } else {
        next.add(channel);
      }
      return next;
    });
  };

  const handleAddType = () => {
    const value = newTypeInput.trim();
    if (!value) return;
    if (!customTypes.includes(value)) {
      setCustomTypes((prev) => [...prev, value]);
      setExpandedChannels((prev) => new Set(prev).add(value));
    }
    setNewTypeInput("");
  };

  const formatChannelLabel = (channel: string) =>
    channel
      .split(/[-_]/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");

  const activeSystemTemplate =
    canManageSystemTemplates && activeSystemId
      ? systemTemplates.find((entry) => entry.id === activeSystemId) ?? null
      : null;
  const showSystemTemplates = allowTemplateAutomation && canManageSystemTemplates;

  const showTemplateDetail = !activeSystemId && !creatorChannel && selectedTemplate;

  const appendToken = (value: string, token: string) => {
    const spacer = value.trim().length ? " " : "";
    return `${value}${spacer}${token}`;
  };

  const SubjectVariableInput = ({
    value,
    onChange,
    placeholder,
    className,
  }: {
    value: string;
    onChange: (next: string) => void;
    placeholder?: string;
    className?: string;
  }) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [range, setRange] = useState<{ start: number; end: number } | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const filtered = useMemo(() => {
      const entries = Object.entries(variables);
      const normalized = query.trim().toLowerCase();
      if (!normalized) return entries;
      return entries.filter(([key, description]) => {
        const haystack = `${key} ${description ?? ""}`.toLowerCase();
        return haystack.includes(normalized);
      });
    }, [query, variables]);

    useEffect(() => {
      if (!open) return;
      setActiveIndex(0);
    }, [open, query]);

    useEffect(() => {
      if (!filtered.length) {
        setActiveIndex(0);
        return;
      }
      setActiveIndex((prev) => Math.min(prev, filtered.length - 1));
    }, [filtered.length]);

    const updateMatch = (nextValue: string, cursor: number | null) => {
      if (!allowTemplateAutomation) return;
      if (cursor === null) {
        setOpen(false);
        return;
      }
      const prefix = nextValue.slice(0, cursor);
      const match = /{{\s*([a-zA-Z0-9._-]*)$/.exec(prefix);
      if (!match) {
        setOpen(false);
        return;
      }
      setRange({ start: cursor - match[0].length, end: cursor });
      setQuery(match[1] ?? "");
      setOpen(true);
    };

    const handleInsert = (token: string) => {
      if (!range) return;
      const insert = `{{ ${token} }}`;
      const next = `${value.slice(0, range.start)}${insert}${value.slice(range.end)}`;
      onChange(next);
      setOpen(false);
      setQuery("");
      setRange(null);
      requestAnimationFrame(() => {
        if (!inputRef.current) return;
        const caret = range.start + insert.length;
        inputRef.current.setSelectionRange(caret, caret);
        inputRef.current.focus();
      });
    };

    return (
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={className}
          value={value}
          placeholder={placeholder}
          onChange={(event) => {
            const nextValue = event.target.value;
            onChange(nextValue);
            updateMatch(nextValue, event.target.selectionStart);
          }}
          onClick={(event) => updateMatch(value, event.currentTarget.selectionStart)}
          onKeyUp={(event) => updateMatch(value, event.currentTarget.selectionStart)}
          onKeyDown={(event) => {
            if (!open) return;
            if (event.key === "Escape") {
              setOpen(false);
              return;
            }
            if (!filtered.length) return;
            if (event.key === "ArrowDown") {
              event.preventDefault();
              setActiveIndex((prev) => (prev + 1) % filtered.length);
            }
            if (event.key === "ArrowUp") {
              event.preventDefault();
              setActiveIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
            }
            if (event.key === "Enter") {
              event.preventDefault();
              const entry = filtered[activeIndex];
              if (entry) handleInsert(entry[0]);
            }
          }}
        />
        {allowTemplateAutomation && open && (
          <div className="absolute right-0 top-full z-10 mt-2 w-72 rounded-xl border border-border/60 bg-background/95 p-2 text-xs shadow-xl">
            <div className="flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Insert variable</p>
              <button
                type="button"
                className="text-[11px] text-muted-foreground hover:text-foreground"
                onClick={() => setOpen(false)}
              >
                Esc
              </button>
            </div>
            <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-border/60 bg-background/40 p-1" role="listbox">
              {variablesLoading ? (
                <p className="p-2 text-[11px] text-muted-foreground">Loading variables…</p>
              ) : variablesError ? (
                <p className="p-2 text-[11px] text-destructive">{variablesError}</p>
              ) : filtered.length === 0 ? (
                <p className="p-2 text-[11px] text-muted-foreground">No matching variables.</p>
              ) : (
                filtered.map(([key, description], index) => (
                  <button
                    key={key}
                    type="button"
                    className={cn(
                      "flex w-full flex-col items-start gap-1 rounded-lg px-2 py-1 text-left",
                      activeIndex === index ? "bg-primary/10" : "hover:bg-primary/10",
                    )}
                    role="option"
                    aria-selected={activeIndex === index}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleInsert(key)}
                  >
                    <span className="font-mono text-[11px] text-success">{`{{ ${key} }}`}</span>
                    {description && <span className="text-[11px] text-muted-foreground">{description}</span>}
                  </button>
                ))
              )}
            </div>
            <p className="mt-2 text-[10px] uppercase tracking-wide text-muted-foreground">
              Tip: type <span className="font-mono">{'{{'}  </span>
            </p>
          </div>
        )}
      </div>
    );
  };

  const VariableInsertPopover = ({
    editorRef,
    onInsertFallback,
  }: {
    editorRef?: React.MutableRefObject<Editor | null>;
    onInsertFallback: (token: string) => void;
  }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const filtered = useMemo(() => {
      const entries = Object.entries(variables);
      const normalized = query.trim().toLowerCase();
      if (!normalized) return entries;
      return entries.filter(([key, description]) => {
        const haystack = `${key} ${description ?? ""}`.toLowerCase();
        return haystack.includes(normalized);
      });
    }, [query, variables]);

    if (!allowTemplateAutomation) {
      return (
        <div className="absolute right-2 top-2">
          <button
            type="button"
            className="rounded-full border border-border/60 bg-background/60 px-2 py-1 text-[10px] font-semibold text-muted-foreground"
            onClick={() => openUpgradeModal("Template automation")}
          >
            Variables locked
          </button>
        </div>
      );
    }

    return (
      <div className="absolute right-2 top-2 z-10">
        <button
          type="button"
          className="rounded-full border border-border/60 bg-background/70 px-2 py-1 text-[10px] font-semibold text-foreground/80 hover:border-primary/60 hover:text-primary"
          onClick={() => setOpen((prev) => !prev)}
        >
          Insert variable
        </button>
        {open && (
          <div className="mt-2 w-72 rounded-xl border border-border/60 bg-background/95 p-2 text-xs shadow-xl">
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search variables…"
              className="w-full rounded-lg border border-border bg-background/50 px-2 py-1 text-[11px] text-foreground"
              autoFocus
            />
            <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-border/60 bg-background/40 p-1">
              {variablesLoading ? (
                <p className="p-2 text-[11px] text-muted-foreground">Loading variables…</p>
              ) : variablesError ? (
                <p className="p-2 text-[11px] text-destructive">{variablesError}</p>
              ) : filtered.length === 0 ? (
                <p className="p-2 text-[11px] text-muted-foreground">No matches.</p>
              ) : (
                filtered.map(([key, description]) => (
                  <button
                    key={key}
                    type="button"
                    className="flex w-full flex-col items-start gap-1 rounded-lg px-2 py-1 text-left hover:bg-primary/10"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      const token = `{{ ${key} }}`;
                      const editor = editorRef?.current;
                      if (editor) {
                        editor.chain().focus().insertContent({ type: "text", text: token }).run();
                      } else {
                        onInsertFallback(token);
                      }
                      setOpen(false);
                      setQuery("");
                    }}
                  >
                    <span className="font-mono text-[11px] text-success">{`{{ ${key} }}`}</span>
                    {description && <span className="text-[11px] text-muted-foreground">{description}</span>}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="aa-glass-card space-y-3 p-3">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Template Manager</h2>
          <p className="text-xs text-muted-foreground">Draft, test, and promote CRM-ready scripts.</p>
        </div>
        <div className="flex gap-2 text-xs">
          <button
            type="button"
            className="rounded-full border border-primary/40 px-3 py-1 text-primary"
            onClick={() => loadTemplates()}
            disabled={templatesState.loading}
          >
            {templatesState.loading ? "Refreshing…" : "Refresh"}
          </button>
          <button
            type="button"
            className="rounded-full border border-border px-3 py-1 text-foreground/80"
            onClick={() => {
              setCreatorChannel("email");
              setSelectedTemplateId(null);
              setActiveSystemId(null);
            }}
          >
            New Template
          </button>
        </div>
      </header>
      <div className="grid gap-3 lg:grid-cols-[240px,1fr]">
        <aside className="space-y-3 rounded-xl border border-border/60 bg-background/60 p-3 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wide text-primary">System templates</p>
            {showSystemTemplates ? (
              systemListLoading ? (
                <p className="mt-2 text-xs text-muted-foreground">Loading…</p>
              ) : (
                <div className="mt-2 space-y-2">
                  {systemTemplates.map((template) => {
                    const isActive = activeSystemId === template.id;
                    return (
                      <button
                        key={template.id}
                        type="button"
                        className={cn(
                          "w-full rounded-lg border px-3 py-2 text-left text-xs transition",
                          isActive
                            ? "border-primary/60 bg-primary/10 text-foreground"
                            : "border-border bg-background/40 text-foreground/70 hover:border-primary/40",
                        )}
                        onClick={() => {
                          setActiveSystemId(template.id);
                          setSelectedTemplateId(null);
                          setCreatorChannel(null);
                        }}
                      >
                        <p className="font-semibold text-sm">{template.label}</p>
                        <p className="text-[11px] text-muted-foreground">{template.description}</p>
                      </button>
                    );
                  })}
                </div>
              )
            ) : (
              <p className="mt-2 text-xs text-muted-foreground">System templates are limited to editor accounts.</p>
            )}
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-primary">Template types</p>
            <div className="mt-2 space-y-3">
              {channelTypes.map((channel) => {
                const templates = templatesByChannel[channel] ?? [];
                const expanded = expandedChannels.has(channel);
                return (
                  <div key={channel} className="rounded-xl border border-border/70 bg-background/40">
                    <button
                      type="button"
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-semibold text-foreground"
                      onClick={() => expandChannel(channel)}
                    >
                      {formatChannelLabel(channel)}
                      <span className="text-xs text-muted-foreground">{expanded ? "−" : "+"}</span>
                    </button>
                    {expanded && (
                      <div className="space-y-1 border-t border-border/60 px-3 py-2 text-xs text-foreground/70">
                        {templates.length ? (
                          templates.map((template) => {
                            const isActive = selectedTemplateId === template.id;
                            return (
                              <button
                                key={template.id}
                                type="button"
                                className={cn(
                                  "w-full rounded-lg border px-2 py-1 text-left",
                                  isActive
                                    ? "border-primary bg-primary/10 text-foreground"
                                    : "border-transparent hover:border-primary/40",
                                )}
                                onClick={() => {
                                  setSelectedTemplateId(template.id);
                                  setCreatorChannel(null);
                                  setActiveSystemId(null);
                                }}
                              >
                                <p className="font-semibold">{template.name}</p>
                                <p className="text-[11px] text-muted-foreground">{template.persona || "General"}</p>
                              </button>
                            );
                          })
                        ) : (
                          <p className="text-muted-foreground">No templates yet.</p>
                        )}
                        <button
                          type="button"
                          className="w-full rounded-lg border border-dashed border-primary/40 px-2 py-1 text-xs font-semibold text-primary hover:border-primary"
                          onClick={() => {
                            setCreatorChannel(channel);
                            setSelectedTemplateId(null);
                            setActiveSystemId(null);
                          }}
                        >
                          + New {formatChannelLabel(channel)} template
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-4 space-y-2 text-xs text-muted-foreground">
              <p className="uppercase tracking-wide">Add type</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newTypeInput}
                  onChange={(event) => setNewTypeInput(event.target.value)}
                  className="w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                  placeholder="esports, routing…"
                />
                <button
                  type="button"
                  className="rounded-full border border-primary/50 px-3 py-1 text-primary"
                  onClick={handleAddType}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </aside>
        <div className="space-y-6">
          {templateActionMessage && <p className="text-xs text-success">{templateActionMessage}</p>}
          {activeSystemTemplate && (
            <section className="space-y-3 rounded-2xl border border-border/60 bg-background/40 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-primary">System template</p>
                  <h3 className="text-lg font-semibold text-foreground">{activeSystemTemplate.label}</h3>
                  <p className="text-xs text-muted-foreground">{activeSystemTemplate.description}</p>
                </div>
                <div className="flex gap-2 text-xs">
                  <button
                    type="button"
                    className="rounded-full border border-border px-3 py-1 text-foreground/80"
                    onClick={() => void loadSystemBody(activeSystemTemplate.id, true)}
                    disabled={systemLoadingId === activeSystemTemplate.id || systemSavingId === activeSystemTemplate.id}
                  >
                    Reload
                  </button>
                  <button
                    type="button"
                    className="rounded-full border border-primary/50 px-3 py-1 text-primary disabled:opacity-60"
                    onClick={() => void handleSystemSave()}
                    disabled={systemSavingId === activeSystemTemplate.id || systemLoadingId === activeSystemTemplate.id}
                  >
                    {systemSavingId === activeSystemTemplate.id ? "Saving…" : "Save template"}
                  </button>
                </div>
              </div>
              {systemMessage && <p className="text-xs text-muted-foreground">{systemMessage}</p>}
              {systemLoadingId === activeSystemTemplate.id ? (
                <p className="text-sm text-muted-foreground">Loading template…</p>
              ) : (
                <RichTextEditor
                  value={systemContentMap[activeSystemTemplate.id] ?? ""}
                  onChange={(next) =>
                    setSystemContentMap((prev) => ({ ...prev, [activeSystemTemplate.id]: next }))
                  }
                  minHeight={320}
                  enableSourceToggle
                />
              )}
            </section>
          )}

          {creatorChannel && (
            <section className="space-y-3 rounded-2xl border border-border/60 bg-background/40 p-4 text-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-primary">New template</p>
                  <h3 className="text-lg font-semibold text-foreground">{formatChannelLabel(creatorChannel)}</h3>
                </div>
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setCreatorChannel(null)}
                >
                  Cancel
                </button>
              </div>
              <div className="grid gap-2 md:grid-cols-2">
                <label className="text-xs text-muted-foreground">
                  Name
                  <input
                    type="text"
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                    value={templateForm.name}
                    onChange={(event) => setTemplateForm((prev) => ({ ...prev, name: event.target.value }))}
                  />
                </label>
                <label className="text-xs text-muted-foreground">
                  Persona
                  <input
                    type="text"
                    className="mt-1 w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                    value={templateForm.persona}
                    onChange={(event) => setTemplateForm((prev) => ({ ...prev, persona: event.target.value }))}
                  />
                </label>
              </div>
              <label className="text-xs text-muted-foreground">
                Subject
                <SubjectVariableInput
                  value={templateForm.subject}
                  onChange={(next) => setTemplateForm((prev) => ({ ...prev, subject: next }))}
                  className="mt-1 w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                />
              </label>
              <div className="relative">
                <label className="text-xs text-muted-foreground">
                  Body
                  <RichTextEditor
                    value={templateForm.body}
                    onChange={(next) => setTemplateForm((prev) => ({ ...prev, body: next }))}
                    placeholder="Write the template body. Use {{ lead.title }} style variables."
                    minHeight={220}
                    className="mt-2"
                    editorRef={templateBodyEditorRef}
                    variableRegistry={variables}
                    enableVariableCommands={allowTemplateAutomation}
                  />
                </label>
                <VariableInsertPopover
                  editorRef={templateBodyEditorRef}
                  onInsertFallback={(token) => setTemplateForm((prev) => ({ ...prev, body: appendToken(prev.body, token) }))}
                />
              </div>
              {allowTemplateAutomation ? (
                <>
                  <DetectedVariableBadges tokens={creationTokens} registry={variables} />
                  {creationLint.length > 0 && (
                    <p className="text-xs text-destructive">
                      Unrecognized variables: {creationLint.join(", ")}
                    </p>
                  )}
                  {creationSyntaxErrors.length > 0 && (
                    <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
                      {creationSyntaxErrors.map((error) => (
                        <p key={error}>{error}</p>
                      ))}
                    </div>
                  )}
                  <div className="rounded-xl border border-border/60 bg-background/40 p-3 text-xs text-foreground/70">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Live preview</p>
                    <p className="mt-1">Subject: {buildPreview(templateForm.subject, variables)}</p>
                    <pre className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-border/70 bg-background/60 p-2">
                      {buildPreview(templateForm.body, variables)}
                    </pre>
                  </div>
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Suggested variables: {Object.keys(variables).slice(0, 8).join(", ") || "—"}
                  </p>
                </>
              ) : (
                <LockedFeatureCard
                  title="Template automation"
                  description="Upgrade to preview resolved variables and lint templates for missing data."
                  variant="inline"
                  onUpgrade={() => openUpgradeModal("Template automation")}
                />
              )}
              <button
                type="button"
                className="rounded-xl bg-primary/20 px-4 py-2 text-sm font-semibold text-primary"
                onClick={handleCreateTemplate}
              >
                Save template
              </button>
            </section>
          )}

          {showTemplateDetail ? (
            <section className="space-y-3 rounded-2xl border border-border/70 bg-background/30 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-primary">Channel</p>
                  <h3 className="text-xl font-semibold text-foreground">
                    {formatChannelLabel(selectedTemplate.channel || "email")}
                  </h3>
                  <p className="text-xs text-muted-foreground">{selectedTemplate.persona || "General persona"}</p>
                </div>
                <div className="flex gap-2 text-xs">
                  <button
                    type="button"
                    className="rounded-full border border-border px-3 py-1 text-foreground/80"
                    onClick={() => setIsEditingTemplate((prev) => !prev)}
                  >
                    {isEditingTemplate ? "Close" : "Edit"}
                  </button>
                  <button
                    type="button"
                    className="rounded-full border border-destructive/40 px-3 py-1 text-destructive"
                    onClick={handleDeleteTemplate}
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    className="rounded-full border border-primary/40 px-3 py-1 text-primary"
                    onClick={handleCloneVariant}
                  >
                    Generate Variant
                  </button>
                </div>
              </div>
              {isEditingTemplate && (
                <div className="grid gap-2 rounded-xl border border-border/60 bg-background/30 p-3 text-xs text-muted-foreground md:grid-cols-3">
                  {(["name", "persona", "channel"] as const).map((field) => (
                    <label key={field}>
                      {field.charAt(0).toUpperCase() + field.slice(1)}
                      <input
                        type="text"
                        className="mt-1 w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                        value={(templateEditValues as Record<string, string>)[field]}
                        onChange={(event) =>
                          setTemplateEditValues((prev) => ({
                            ...prev,
                            [field]: event.target.value,
                          }))
                        }
                      />
                    </label>
                  ))}
                  <div className="md:col-span-3">
                    <button
                      type="button"
                      className="rounded-xl bg-primary/20 px-4 py-2 text-sm font-semibold text-primary"
                      onClick={handleUpdateTemplate}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              )}
              <div className="space-y-3">
                {versionsForTemplate.length ? (
                  versionsForTemplate.map((version) => (
                    <div key={version.id} className="rounded-xl border border-border/60 bg-background/40 p-3 text-sm text-foreground/80">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span className="text-base font-semibold text-foreground">Version {version.version}</span>
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] uppercase tracking-wide ${
                              version.active ? "bg-success/50 text-success" : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {version.active ? "Active" : "Inactive"}
                          </span>
                        </div>
                        <div className="flex gap-2 text-xs">
                          <button type="button" className="rounded-full border border-border px-3 py-1 text-foreground/80" onClick={() => startVersionEdit(version)}>
                            Edit
                          </button>
                          <button
                            type="button"
                            className="rounded-full border border-destructive/40 px-3 py-1 text-destructive"
                            onClick={() => handleDeleteVersion(version.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      {versionEditingId === version.id ? (
                        <div className="mt-3 space-y-2 rounded-xl border border-border/70 bg-background/40 p-3 text-xs text-foreground/70">
                          <label className="block">
                            Subject
                            <SubjectVariableInput
                              value={versionForm.subject}
                              onChange={(next) => setVersionForm((prev) => ({ ...prev, subject: next }))}
                              className="mt-1 w-full rounded-lg border border-border bg-background/40 px-2 py-1 text-foreground"
                            />
                          </label>
                          <div className="relative">
                          <label className="block">
                            Body
                            <RichTextEditor
                              value={versionForm.body}
                              onChange={(next) => setVersionForm((prev) => ({ ...prev, body: next }))}
                              placeholder="Body content for this version… Use {{ token }} variables."
                              minHeight={200}
                              className="mt-2"
                              editorRef={versionBodyEditorRef}
                              variableRegistry={variables}
                              enableVariableCommands={allowTemplateAutomation}
                            />
                          </label>
                            <VariableInsertPopover
                              editorRef={versionBodyEditorRef}
                              onInsertFallback={(token) =>
                                setVersionForm((prev) => ({ ...prev, body: appendToken(prev.body, token) }))
                              }
                            />
                          </div>
                          <label className="flex items-center gap-2 text-muted-foreground">
                            <input
                              type="checkbox"
                              checked={versionForm.active}
                              onChange={(event) => setVersionForm((prev) => ({ ...prev, active: event.target.checked }))}
                              className="h-4 w-4 rounded border-border bg-background/40"
                            />
                            Active
                          </label>
                          {allowTemplateAutomation ? (
                            <>
                              <DetectedVariableBadges tokens={versionTokens} registry={variables} />
                              {versionEditingId === version.id && versionLint.length > 0 && (
                                <p className="text-xs text-destructive">
                                  Unrecognized variables: {versionLint.join(", ")}
                                </p>
                              )}
                              {versionEditingId === version.id && versionSyntaxErrors.length > 0 && (
                                <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
                                  {versionSyntaxErrors.map((error) => (
                                    <p key={error}>{error}</p>
                                  ))}
                                </div>
                              )}
                              <div className="rounded-lg border border-border/70 bg-background/50 p-3 text-xs text-foreground/70">
                                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Live preview</p>
                                <p className="mt-1">Subject: {buildPreview(versionForm.subject, variables)}</p>
                                <pre className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-border/60 bg-background/30 p-2">
                                  {buildPreview(versionForm.body, variables)}
                                </pre>
                              </div>
                            </>
                          ) : (
                            <LockedFeatureCard
                              title="Template automation"
                              description="Upgrade to lint template versions and preview resolved output."
                              variant="inline"
                              onUpgrade={() => openUpgradeModal("Template automation")}
                            />
                          )}
                          <div className="flex gap-2">
                            <Button type="button" size="sm" variant="success" onClick={handleVersionUpdate}>
                              Save Version
                            </Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => setVersionEditingId(null)}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 space-y-2 text-foreground/70">
                          <div>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">Subject</p>
                            <p className="text-sm text-foreground">{version.subject_template || "Untitled subject"}</p>
                            {allowTemplateAutomation &&
                              (() => {
                                const detected = (version.metadata?.template_variables?.all as string[] | undefined) ?? [];
                                if (!detected.length) return null;
                                const registryMap = detected.reduce<Record<string, string>>((acc, token) => {
                                  acc[token] = version.warnings?.includes(token) ? "" : "known";
                                  return acc;
                                }, {});
                                return <DetectedVariableBadges tokens={detected} registry={registryMap} />;
                              })()}
                          </div>
                          <div>
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">Body</p>
                            <div className="rounded-lg border border-border/60 bg-background/40 p-3 text-sm text-foreground/80">
                              <div dangerouslySetInnerHTML={{ __html: version.body_template || "<em>No body provided.</em>" }} />
                            </div>
                            {allowTemplateAutomation && (
                              <DetectedVariableBadges
                                tokens={extractTemplateTokens(version.subject_template || "", version.body_template || "")}
                                registry={Object.fromEntries(
                                  (version.metadata?.template_variables?.all as string[] | undefined)?.map((token) => [
                                    token,
                                    "known",
                                  ]) ?? [],
                                )}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No versions yet.</p>
                )}
              </div>
            </section>
          ) : null}

          {!activeSystemTemplate && !creatorChannel && !selectedTemplate && (
            <div className="rounded-2xl border border-dashed border-border/50 bg-background/30 p-6 text-center text-sm text-muted-foreground">
              Pick a template type or script on the left to start editing.
            </div>
          )}

        </div>
      </div>
    </section>
  );
}
 
